"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { mutate } from "swr";
import { useRouter } from "i18n/routing";

/**
 * Jerly Widget - stays mounted in the layout and listens for data changes from pages.
 * This component never unmounts, ensuring the widget iframe persists across navigation.
 *
 * Pattern: Publisher-Listener (B2C Version)
 * - This is the Listener (in layout, listens to events)
 * - JerlyPublisher is the Publisher (inside Providers, has access to hooks)
 * - B2C Specific: Widget always visible, works with anonymous carts
 */

export default function JerlyWidget() {
  const router = useRouter();
  const locale = useLocale();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [widgetReady, setWidgetReady] = useState(false);

  // Widget data that gets updated from events
  const [widgetData, setWidgetData] = useState<{
    userId?: string;
    cartId?: string;
    wishlistId?: string;
    locale?: string;
  }>({
    locale,
  });

  // Listen for data changes from pages (via JerlyPublisher)
  useEffect(() => {
    const handleDataChange = (event: CustomEvent) => {
      const {
        userId,
        cartId,
        wishlistId,
        locale: newLocale,
      } = event.detail || {};

      console.log("COMMERCETOOLS 🔄 Widget: Datos actualizados desde página:", {
        userId: userId || "(anónimo)",
        cartId: cartId || "(pendiente)",
        wishlistId: wishlistId || "(ninguno)",
        timestamp: new Date().toISOString(),
      });

      setWidgetData({
        userId,
        cartId,
        wishlistId,
        locale: newLocale || locale,
      });
    };

    window.addEventListener(
      "widget-data-changed",
      handleDataChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "widget-data-changed",
        handleDataChange as EventListener
      );
    };
  }, [locale]);

  // Build widget URL (static parameters only, data is sent via postMessage)
  // IMPORTANT: Only include static params in URL. Dynamic data (userId, cartId, wishlistId)
  // is sent via postMessage to prevent iframe reloads when data changes
  const widgetUrl =
    "https://www.widget.jerly.ai/widget?" +
    // 'http://localhost:3001/widget?' +
    `locale=${locale}` +
    `&platform=commercetools` +
    `&privateKey=${process.env.NEXT_PUBLIC_VOICE_PRIVATE_KEY || ""}` +
    `&publicKey=${process.env.NEXT_PUBLIC_VOICE_PUBLIC_KEY || ""}`;

  // Listen to widget messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;

      // Ready signal: we can send configuration
      if (data?.type === "WIDGET_READY") {
        setWidgetReady(true);
        return;
      }

      // SPA navigation requested by the widget (preferred)
      if (data?.type === "WIDGET_ROUTE_PUSH") {
        const requestedPath = data.path;
        if (requestedPath) {
          router.push(requestedPath);
        }
        return;
      }

      // Dynamic resizing: width
      if (data?.type === "WIDGET_SIZE") {
        const { width } = data || {};
        if (iframeRef.current && width) {
          iframeRef.current.style.width = `${Math.max(80, Number(width))}px`;
        }
        return;
      }

      if (data?.type === "WIDGET_OPEN_STATE") {
        const { open, minimized, initial } = data;
        if (iframeRef.current) {
          // Manejar tres estados: minimizado (74px), cerrado (altura inicial), abierto (90vh)
          if (minimized) {
            iframeRef.current.style.height = "74px";
          } else if (open) {
            iframeRef.current.style.height = "90vh";
          } else if (initial) {
            iframeRef.current.style.height = "200px";
            iframeRef.current.style.width = "290px"; // Ancho inicial
          } else {
            iframeRef.current.style.height = "100px"; // Altura inicial
            iframeRef.current.style.width = "100px"; // Ancho inicial
          }
        }
        return;
      }

      // 🛒 Cart updated: refresh SWR cache to update navbar badge
      // This handles the WIDGET_CART_UPDATED message from the widget when a product
      // is added to cart (via voice or button). We invalidate the SWR cache so
      // the header/navbar automatically refreshes the cart count.
      if (data?.type === "WIDGET_CART_UPDATED") {
        const { cartId, itemCount, totalPrice, currency } = data;

        console.log("COMMERCETOOLS 🛒 Widget: Carrito actualizado desde widget:", {
          cartId,
          itemCount,
          totalPrice,
          currency,
          timestamp: new Date().toISOString(),
        });

        // Invalidate SWR cache to refresh cart data in the navbar
        // This triggers a re-fetch of cart data across all components using useCart()
        mutate("/action/cart/getCart");

        // Also emit custom event for any additional listeners
        const cartUpdateEvent = new CustomEvent("cart-updated-from-widget", {
          detail: {
            cartId,
            itemCount,
            totalPrice,
            currency,
          },
        });
        window.dispatchEvent(cartUpdateEvent);
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [router]);

  // Update widget when data changes or widget becomes ready
  // B2C: Always send config when widget is ready, even with partial data
  useEffect(() => {
    if (!widgetReady) return;
    if (!iframeRef.current?.contentWindow) return;

    const config = {
      locale: widgetData.locale || locale,
      platform: "commercetools",
      privateKey: process.env.NEXT_PUBLIC_VOICE_PRIVATE_KEY,
      publicKey: process.env.NEXT_PUBLIC_VOICE_PUBLIC_KEY,
      customData: {
        userId: widgetData.userId || undefined,
        cartId: widgetData.cartId || undefined,
        wishlistId: widgetData.wishlistId || undefined,
      },
    };

    console.log(
      "COMMERCETOOLS 📤 VoiceWidget: Enviando configuración al widget:",
      {
        type: "WIDGET_CONFIG",
        config,
        hasCartId: !!widgetData.cartId,
        timestamp: new Date().toISOString(),
      }
    );

    iframeRef.current.contentWindow.postMessage(
      { type: "WIDGET_CONFIG", config },
      "*"
    );
  }, [widgetData, widgetReady, locale]);

  return (
    <iframe
      id="voice-widget"
      ref={iframeRef}
      src={widgetUrl}
      frameBorder="0"
      onLoad={() => {
        console.log(
          "COMMERCETOOLS 📥 VoiceWidget: Iframe cargado (B2C - siempre visible)"
        );
      }}
      allow="microphone"
      style={{
        position: "fixed",
        bottom: "10px",
        right: "20px",
        zIndex: 9999,
        width: "300px",
        height: "160px",
        border: "none",
        // B2C: Widget always visible, even while cart is being created
        display: "block",
      }}
    />
  );
}